import { ClientAccount } from '@/shared/clients/client-account';
import { ClientPartner } from '@/shared/clients/client-partner';
import { ClientPowerOfAttorney } from '@/shared/clients/client-power-of-attorney';
import { ClientMergeConflict } from '@/shared/data-management/client-merge-conflict';
import { ClientRelationshipType } from '@/shared/types/client-relationship-type';
import { ClientStatusType } from '@/shared/types/client-status-type';
import { ClientTitleType } from '@/shared/types/client-title-type';
import store from '@/store';
import { DateTime } from 'luxon';

export function findPriorityClientAccount(clientAccountOne: ClientAccount, clientAccountTwo: ClientAccount): string {
    if (clientAccountOne.will != null || clientAccountTwo.will != null) {
        if (clientAccountOne.will != null && clientAccountTwo.will == null) {
            return clientAccountOne.id!;
        }

        if (clientAccountOne.will == null && clientAccountTwo.will != null) {
            return clientAccountTwo.id!;
        }

        return clientAccountOne.will!.date! <= clientAccountTwo.will!.date! ? clientAccountOne.id! : clientAccountTwo.id!;
    }

    if (clientAccountOne.powerOfAttorneys.length > 0 || clientAccountTwo.powerOfAttorneys.length > 0) {
        const clientAccountOnePowerOfAttorney = earliestPowerOfAttorney(clientAccountOne.powerOfAttorneys);
        const clientAccountTwoPowerOfAttorney = earliestPowerOfAttorney(clientAccountTwo.powerOfAttorneys);

        if (clientAccountOnePowerOfAttorney != null && clientAccountTwoPowerOfAttorney == null) {
            return clientAccountOne.id!;
        }

        if (clientAccountOnePowerOfAttorney == null && clientAccountTwoPowerOfAttorney != null) {
            return clientAccountTwo.id!;
        }

        if (clientAccountOnePowerOfAttorney!.signatureDate != null && clientAccountTwoPowerOfAttorney!.signatureDate == null) {
            return clientAccountOne.id!;
        }

        if (clientAccountOnePowerOfAttorney!.signatureDate == null && clientAccountTwoPowerOfAttorney!.signatureDate != null) {
            return clientAccountTwo.id!;
        }

        return clientAccountOnePowerOfAttorney!.signatureDate! <= clientAccountTwoPowerOfAttorney!.signatureDate! ? clientAccountOne.id! : clientAccountTwo.id!;
    }

    return clientAccountOne.createdDate! <= clientAccountTwo.createdDate! ? clientAccountOne.id! : clientAccountTwo.id!;
}

export function findMergeConflicts(priorityClient: ClientAccount, mergeClient: ClientAccount): Array<ClientMergeConflict> {
    const conflicts = new Array<ClientMergeConflict>();

    if (!isNullOrWhitespace(priorityClient.titleId) && !isNullOrWhitespace(mergeClient.titleId) && priorityClient.titleId !== mergeClient.titleId) {
        conflicts.push({ fieldName: 'titleId', displayName: 'Title', priorityValue: clientTitleTypeDescription(priorityClient.titleId!), mergeValue: clientTitleTypeDescription(mergeClient.titleId!), useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.accountStatusId) && !isNullOrWhitespace(mergeClient.accountStatusId) && priorityClient.accountStatusId !== mergeClient.accountStatusId) {
        conflicts.push({ fieldName: 'accountStatusId', displayName: 'Account Status', priorityValue: clientStatusTypeDescription(priorityClient.accountStatusId), mergeValue: clientStatusTypeDescription(mergeClient.accountStatusId), useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.relationshipStatusId) && !isNullOrWhitespace(mergeClient.relationshipStatusId) && priorityClient.relationshipStatusId !== mergeClient.relationshipStatusId) {
        conflicts.push({ fieldName: 'relationshipStatusId', displayName: 'Relationship Status', priorityValue: clientRelationshipTypeDescription(priorityClient.relationshipStatusId), mergeValue: clientRelationshipTypeDescription(mergeClient.relationshipStatusId), useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.firstName) && !isNullOrWhitespace(mergeClient.firstName) && priorityClient.firstName !== mergeClient.firstName) {
        conflicts.push({ fieldName: 'firstName', displayName: 'First Name', priorityValue: priorityClient.firstName, mergeValue: mergeClient.firstName, useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.middleNames) && !isNullOrWhitespace(mergeClient.middleNames) && priorityClient.middleNames !== mergeClient.middleNames) {
        conflicts.push({ fieldName: 'middleNames', displayName: 'Middle Name(s)', priorityValue: priorityClient.middleNames, mergeValue: mergeClient.middleNames, useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.lastName) && !isNullOrWhitespace(mergeClient.lastName) && priorityClient.lastName !== mergeClient.lastName) {
        conflicts.push({ fieldName: 'lastName', displayName: 'Last Name', priorityValue: priorityClient.lastName, mergeValue: mergeClient.lastName, useMergeValue: null });
    }

    if (priorityClient.dateOfBirth != null && mergeClient.dateOfBirth != null && priorityClient.dateOfBirth !== mergeClient.dateOfBirth) {
        conflicts.push({ fieldName: 'dateOfBirth', displayName: 'Date of Birth', priorityValue: convertDateToString(priorityClient.dateOfBirth), mergeValue: convertDateToString(mergeClient.dateOfBirth), useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.email) && !isNullOrWhitespace(mergeClient.email) && priorityClient.email !== mergeClient.email) {
        conflicts.push({ fieldName: 'email', displayName: 'Email', priorityValue: priorityClient.email, mergeValue: mergeClient.email, useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.telephone) && !isNullOrWhitespace(mergeClient.telephone) && priorityClient.telephone !== mergeClient.telephone) {
        conflicts.push({ fieldName: 'telephone', displayName: 'Telephone', priorityValue: priorityClient.telephone, mergeValue: mergeClient.telephone, useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.mobile) && !isNullOrWhitespace(mergeClient.mobile) && priorityClient.mobile !== mergeClient.mobile) {
        conflicts.push({ fieldName: 'mobile', displayName: 'Mobile', priorityValue: priorityClient.mobile, mergeValue: mergeClient.mobile, useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.occupation) && !isNullOrWhitespace(mergeClient.occupation) && priorityClient.occupation !== mergeClient.occupation) {
        conflicts.push({ fieldName: 'occupation', displayName: 'Occupation', priorityValue: priorityClient.occupation, mergeValue: mergeClient.occupation, useMergeValue: null });
    }

    if (!isNullOrWhitespace(priorityClient.nationalInsuranceNumber) && !isNullOrWhitespace(mergeClient.nationalInsuranceNumber) && priorityClient.nationalInsuranceNumber !== mergeClient.nationalInsuranceNumber) {
        conflicts.push({ fieldName: 'nationalInsuranceNumber', displayName: 'National Insurance Number', priorityValue: priorityClient.nationalInsuranceNumber, mergeValue: mergeClient.nationalInsuranceNumber, useMergeValue: null });
    }

    if (priorityClient.isSensitive !== mergeClient.isSensitive) {
        conflicts.push({ fieldName: 'isSensitive', displayName: 'Sesitive', priorityValue: convertBooleanToString(priorityClient.isSensitive), mergeValue: convertBooleanToString(mergeClient.isSensitive), useMergeValue: null });
    }

    if (priorityClient.hasCourtOfProtection !== mergeClient.hasCourtOfProtection) {
        conflicts.push({ fieldName: 'hasCourtOfProtection', displayName: 'Court of Protection', priorityValue: convertBooleanToString(priorityClient.hasCourtOfProtection), mergeValue: convertBooleanToString(mergeClient.hasCourtOfProtection), useMergeValue: null });
    }

    if (priorityClient.hasChildren != null && mergeClient.hasChildren != null && priorityClient.hasChildren !== mergeClient.hasChildren) {
        conflicts.push({ fieldName: 'hasChildren', displayName: 'Children', priorityValue: convertBooleanToString(priorityClient.hasChildren), mergeValue: convertBooleanToString(mergeClient.hasChildren), useMergeValue: null });
    }

    if (priorityClient.hasChildrenUnderEighteen != null && mergeClient.hasChildrenUnderEighteen != null && priorityClient.hasChildrenUnderEighteen !== mergeClient.hasChildrenUnderEighteen) {
        conflicts.push({ fieldName: 'hasChildrenUnderEighteen', displayName: 'Children Under 18', priorityValue: convertBooleanToString(priorityClient.hasChildrenUnderEighteen), mergeValue: convertBooleanToString(mergeClient.hasChildrenUnderEighteen), useMergeValue: null });
    }

    if (priorityClient.isHomeOwner != null && mergeClient.isHomeOwner != null && priorityClient.isHomeOwner !== mergeClient.isHomeOwner) {
        conflicts.push({ fieldName: 'isHomeOwner', displayName: 'Homeowner', priorityValue: convertBooleanToString(priorityClient.isHomeOwner), mergeValue: convertBooleanToString(mergeClient.isHomeOwner), useMergeValue: null });
    }

    if (priorityClient.isBusinessOwner != null && mergeClient.isBusinessOwner != null && priorityClient.isBusinessOwner !== mergeClient.isBusinessOwner) {
        conflicts.push({ fieldName: 'isBusinessOwner', displayName: 'Business Owner', priorityValue: convertBooleanToString(priorityClient.isBusinessOwner), mergeValue: convertBooleanToString(mergeClient.isBusinessOwner), useMergeValue: null });
    }

    if (priorityClient.estimatedEstateValue != null && mergeClient.estimatedEstateValue != null && priorityClient.estimatedEstateValue !== mergeClient.estimatedEstateValue) {
        conflicts.push({ fieldName: 'estimatedEstateValue', displayName: 'Estimated Estate Value', priorityValue: priorityClient.estimatedEstateValue, mergeValue: mergeClient.estimatedEstateValue, useMergeValue: null });
    }

    if (priorityClient.canPost !== mergeClient.canPost) {
        conflicts.push({ fieldName: 'canPost', displayName: 'Can Post', priorityValue: convertBooleanToString(priorityClient.canPost), mergeValue: convertBooleanToString(mergeClient.canPost), useMergeValue: null });
    }

    if (priorityClient.canSms !== mergeClient.canSms) {
        conflicts.push({ fieldName: 'canSms', displayName: 'Can SMS', priorityValue: convertBooleanToString(priorityClient.canSms), mergeValue: convertBooleanToString(mergeClient.canSms), useMergeValue: null });
    }

    if (priorityClient.canEmail !== mergeClient.canEmail) {
        conflicts.push({ fieldName: 'canEmail', displayName: 'Can Email', priorityValue: convertBooleanToString(priorityClient.canEmail), mergeValue: convertBooleanToString(mergeClient.canEmail), useMergeValue: null });
    }

    if (priorityClient.canTelephone !== mergeClient.canTelephone) {
        conflicts.push({ fieldName: 'canTelephone', displayName: 'Can Telephone', priorityValue: convertBooleanToString(priorityClient.canTelephone), mergeValue: convertBooleanToString(mergeClient.canTelephone), useMergeValue: null });
    }

    if (priorityClient.addressLine1 !== mergeClient.addressLine1 ||
        priorityClient.addressLine2 !== mergeClient.addressLine2 ||
        priorityClient.addressLine3 !== mergeClient.addressLine3 ||
        priorityClient.town !== mergeClient.town ||
        priorityClient.county !== mergeClient.county ||
        priorityClient.postcode !== mergeClient.postcode ||
        priorityClient.isForeignAddress !== mergeClient.isForeignAddress ||
        priorityClient.foreignAddress !== mergeClient.foreignAddress) {
        conflicts.push({ fieldName: 'clientAddress', displayName: 'Address', priorityValue: addressLine(priorityClient), mergeValue: addressLine(mergeClient), useMergeValue: null });
    }

    if (priorityClient.partner != null && mergeClient.partner != null && priorityClient.partner.id !== mergeClient.partner.id) {
        conflicts.push({ fieldName: 'clientPartner', displayName: 'Partner', priorityValue: clientPartnerDescription(priorityClient.partner), mergeValue: clientPartnerDescription(mergeClient.partner), useMergeValue: null });
    }

    return conflicts;
}

function isNullOrWhitespace(value?: string | null): boolean {
    if (value == null) {
        return true;
    }

    return value.trim().length < 1;
}

function earliestPowerOfAttorney(powerOfAttorneys: Array<ClientPowerOfAttorney>): ClientPowerOfAttorney | null {
    let earliest: ClientPowerOfAttorney | null = null;

    for (const powerOfAttorney of powerOfAttorneys) {
        if (earliest == null) {
            earliest = powerOfAttorney;
            continue;
        }

        if (powerOfAttorney.signatureDate != null && (earliest.signatureDate == null || powerOfAttorney.signatureDate < earliest.signatureDate)) {
            earliest = powerOfAttorney;
        }
    }

    return earliest;
}

function clientTitleTypeDescription(titleId: string | null): string {
    if (titleId == null) {
        return '';
    }

    const clientTitleTypes = store.getters['types/clientTitleTypes'] as Array<ClientTitleType>;
    const clientTitleType = clientTitleTypes.find((type) => type.id === titleId);

    return clientTitleType == null ? '' : clientTitleType.value;
}

function clientStatusTypeDescription(accountStatusId: string | null): string {
    if (accountStatusId == null) {
        return '';
    }

    const clientStatusTypes = store.getters['types/clientStatusTypes'] as Array<ClientStatusType>;
    const clientStatusType = clientStatusTypes.find((type) => type.id === accountStatusId);

    return clientStatusType == null ? '' : clientStatusType.value;
}

function clientRelationshipTypeDescription(relationshipStatusId: string | null): string {
    if (relationshipStatusId == null) {
        return '';
    }

    const clientRelationshipTypes = store.getters['types/clientRelationshipTypes'] as Array<ClientRelationshipType>;
    const clientRelationshipType = clientRelationshipTypes.find((type) => type.id === relationshipStatusId);

    return clientRelationshipType == null ? '' : clientRelationshipType.value;
}

function clientPartnerDescription(clientPartner: ClientPartner): string {
    return `${clientPartner.accountNo} - ${clientPartner.firstName == null ? '' : clientPartner.firstName.trim()} ${clientPartner.lastName == null ? '' : clientPartner.lastName.trim()}`;
}

function convertDateToString(date: string | Date | null): string {
    if (date == null) {
        return '';
    }

    return date instanceof Date
        ? DateTime.fromJSDate(date, { zone: 'UTC' }).toLocal().setLocale('en-gb').toFormat('dd/MM/yyyy')
        : DateTime.fromISO(date, { zone: 'UTC' }).toLocal().setLocale('en-gb').toFormat('dd/MM/yyyy');
}

function convertBooleanToString(value: boolean | null): string {
    if (value == null) {
        return 'Unknown';
    }

    return value ? 'Yes' : 'No';
}

function addressLine(client: ClientAccount): string {
    if (client.isForeignAddress) {
        return client.foreignAddress!;
    }

    let formattedLine = `${client.addressLine1!.trim()},`;

    if (!isNullOrWhitespace(client.addressLine2)) {
        formattedLine += ` ${client.addressLine2!.trim()},`;
    }

    if (!isNullOrWhitespace(client.addressLine3)) {
        formattedLine += ` ${client.addressLine3!.trim()},`;
    }

    if (!isNullOrWhitespace(client.town)) {
        formattedLine += ` ${client.town!.trim()},`;
    }

    if (!isNullOrWhitespace(client.county)) {
        formattedLine += ` ${client.county!.trim()},`;
    }

    formattedLine += ` ${client.postcode!.trim()}`;

    return formattedLine;
}
